import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, X, Send } from 'lucide-react';
import { getToken } from '../api';

const BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000/api');

const TIPOS = [
  { value: 'sugerencia', label: '💡 Sugerencia', desc: 'Algo que te gustaría que existiera' },
  { value: 'mejora',     label: '🔧 Mejora',     desc: 'Algo que ya existe pero podría ser mejor' },
  { value: 'bug',        label: '🐛 Error',       desc: 'Algo que no funciona como debería' },
  { value: 'otro',       label: '💬 Otro',        desc: 'Cualquier comentario general' },
];

export default function FeedbackButton({ pantalla }) {
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState('sugerencia');
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState(null);

  const abrir = () => { setAbierto(true); setEnviado(false); setError(null); };
  const cerrar = () => { setAbierto(false); setMensaje(''); setTipo('sugerencia'); setEnviado(false); setError(null); };

  useEffect(() => {
    if (!abierto) return;
    const handle = (e) => { if (e.key === 'Escape') cerrar(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [abierto]); // eslint-disable-line

  const enviar = async () => {
    if (mensaje.trim().length < 5) { setError('Escribe al menos 5 caracteres.'); return; }
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${BASE_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ tipo, pantalla: pantalla || null, mensaje: mensaje.trim() }),
      });
      if (!r.ok) throw new Error('Error al enviar');
      setEnviado(true);
      setTimeout(cerrar, 2200);
    } catch {
      setError('No se pudo enviar. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={abrir}
        title="Sugerencias y mejoras"
        className="fixed bottom-20 md:bottom-6 right-6 z-[900] flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg shadow-indigo-500/30 transition-all hover:scale-105 active:scale-95"
      >
        <MessageSquarePlus size={17} />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      {/* Modal */}
      {abierto && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[1200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-800 text-base">¿Tienes una sugerencia?</h3>
                <p className="text-gray-400 text-xs mt-0.5">Tu opinión nos ayuda a mejorar Choyero!</p>
              </div>
              <button onClick={cerrar} className="text-gray-400 hover:text-gray-600 transition"><X size={20} /></button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {enviado ? (
                <div className="text-center py-6">
                  <div className="text-4xl mb-3">🎉</div>
                  <p className="font-semibold text-gray-800">¡Gracias por tu feedback!</p>
                  <p className="text-gray-400 text-sm mt-1">Lo revisamos con mucha atención.</p>
                </div>
              ) : (
                <>
                  {/* Tipo */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-2">¿De qué tipo es?</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TIPOS.map(t => (
                        <button
                          key={t.value}
                          onClick={() => setTipo(t.value)}
                          className={`text-left px-3 py-2.5 rounded-xl border text-xs transition ${
                            tipo === t.value
                              ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <span className="block font-medium">{t.label}</span>
                          <span className="block text-gray-400 text-[11px] mt-0.5 leading-tight">{t.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Mensaje */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Cuéntanos más <span className="text-gray-400 font-normal">(mínimo 5 caracteres)</span>
                    </label>
                    <textarea
                      value={mensaje}
                      onChange={e => { setMensaje(e.target.value); setError(null); }}
                      placeholder="Describe tu idea, el problema que encontraste o cómo mejorarías algo..."
                      rows={4}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 resize-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none transition"
                    />
                    <div className="flex items-center justify-between mt-1">
                      {error
                        ? <p className="text-red-500 text-xs">{error}</p>
                        : <span />
                      }
                      <span className="text-gray-300 text-xs">{mensaje.length}/500</span>
                    </div>
                  </div>

                  {/* Botones */}
                  <div className="flex gap-3 pt-1">
                    <button
                      onClick={cerrar}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-500 text-sm hover:bg-gray-50 transition"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={enviar}
                      disabled={enviando || mensaje.trim().length < 5}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-xl disabled:opacity-50 transition"
                    >
                      {enviando ? (
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send size={15} />
                      )}
                      {enviando ? 'Enviando...' : 'Enviar'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
